"use client";

import Link from "next/link";
import {
  Globe,
  Instagram,
  Facebook,
  ExternalLink,
  ArrowRight,
  Eye,
  Handshake,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShopAvatar } from "./ShopAvatar";
import { getCountryName, getCountryFlagEmoji } from "@/lib/community/utils";
import { CommunityImage } from "./CommunityImage";
import { getCommunityProductHref, withCommunityReturnTo } from "@/lib/community/navigation";
import type { PublicCommunityShopWithProducts } from "@/lib/community/types";

interface ShopCardProps {
  shop: PublicCommunityShopWithProducts;
  returnTo?: string;
}

const linkConfig = [
  { key: "website_url" as const, label: "Website", icon: Globe },
  { key: "instagram_url" as const, label: "Instagram", icon: Instagram },
  { key: "facebook_url" as const, label: "Facebook", icon: Facebook },
  { key: "other_url" as const, label: "Other", icon: ExternalLink },
];

export function ShopCard({ shop, returnTo }: ShopCardProps) {
  const shopHref = shop.slug
    ? withCommunityReturnTo(`/community/${shop.slug}`, returnTo)
    : undefined;
  const products = shop.products ?? [];
  const hasProducts = products.length > 0;

  const partnerHref = (() => {
    const url = new URL("https://apps.shopify.com/buddify");
    url.searchParams.set("utm_source", "buddify-community");
    url.searchParams.set("utm_campaign", "partner");
    if (shop.slug) {
      url.searchParams.set("utm_content", shop.slug);
    }
    return url.toString();
  })();

  return (
    <Card className="overflow-hidden rounded-2xl border-line bg-panel p-4 shadow-sm sm:p-5 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        {shopHref ? (
          <Link
            href={shopHref}
            className="flex items-center gap-3 min-w-0 group"
          >
            <ShopAvatar shop={shop} size="lg" />

            <div className="min-w-0">
              <span className="block font-semibold text-ink truncate group-hover:text-brand-green transition-colors">
                {shop.name ?? "Unnamed shop"}
              </span>
              {shop.country_code && (
                <span className="block text-sm text-muted mt-0.5">
                  {getCountryFlagEmoji(shop.country_code)}{" "}
                  {getCountryName(shop.country_code) ?? shop.country_code}
                </span>
              )}
            </div>
          </Link>
        ) : (
          <div className="flex items-center gap-3 min-w-0">
            <ShopAvatar shop={shop} size="lg" />

            <div className="min-w-0">
              <span className="block font-semibold text-ink truncate">
                {shop.name ?? "Unnamed shop"}
              </span>
              {shop.country_code && (
                <span className="block text-sm text-muted mt-0.5">
                  {getCountryFlagEmoji(shop.country_code)}{" "}
                  {getCountryName(shop.country_code) ?? shop.country_code}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            href={partnerHref}
            external
            variant="primary"
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            <Handshake className="w-4 h-4 mr-1.5" />
            Partner <span className="hidden sm:inline">on Buddify</span>
          </Button>

          {shopHref && (
            <Button
              href={shopHref}
              variant="secondary"
              size="sm"
              className="flex-1 sm:flex-initial"
            >
              <Eye className="w-4 h-4 mr-1.5" />
              View brand
            </Button>
          )}
        </div>
      </div>

      <hr className="border-line/60 mb-4" />

      {/* Body */}
      <div className="grid lg:grid-cols-12 gap-5">
        {/* Info */}
        <div className="lg:col-span-3 space-y-3">
          {shop.description && (
            <p className="line-clamp-2 sm:line-clamp-3 text-sm text-muted">
              {shop.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {linkConfig.map(({ key, label, icon: Icon }) => {
              const url = shop.links?.[key];
              if (!url) return null;
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-muted hover:text-brand-green transition-colors"
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </a>
              );
            })}
          </div>
        </div>

        {/* Products */}
        <div className="lg:col-span-9">
          {hasProducts ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {products.slice(0, 10).map((product, index) => {
                  const productHref = getCommunityProductHref(shop.slug, product.handle, returnTo);

                  const imageContent = (
                    <CommunityImage
                      src={product.featured_image_url}
                      alt={product.title ?? "Product"}
                      fill
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                      className="rounded-none"
                      containerClassName="absolute inset-0"
                    />
                  );

                  return productHref ? (
                    <Link
                      key={product.id}
                      href={productHref}
                      className={`${index >= 4 ? "hidden sm:block" : "block"} relative aspect-square rounded-xl overflow-hidden bg-raised border border-line hover:opacity-90 transition-opacity`}
                    >
                      {imageContent}
                    </Link>
                  ) : (
                    <div
                      key={product.id}
                      className={`${index >= 4 ? "hidden sm:block" : "block"} relative aspect-square rounded-xl overflow-hidden bg-raised border border-line`}
                    >
                      {imageContent}
                    </div>
                  );
                })}
              </div>
              {shopHref && (
                <div className="flex justify-end mt-3">
                  <Link
                    href={shopHref}
                    className="inline-flex items-center gap-1 text-sm font-medium text-brand-green hover:text-brand-green transition-colors"
                  >
                    See all products
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted bg-page/60 rounded-xl">
              No product previews available.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
